import { ReactNode, useEffect, useState } from 'react';
import { Box, Flex, Spinner } from '@radix-ui/themes';
import { useLocation } from 'react-router-dom';

import { ResumeLockScreen } from 'candidate/core/resume-lock-screen';
import { useRootStore } from 'core/hooks/use-root-store';
import { loadCreateResumeProfileAccess } from './create-resume-profile-access';

type GuardState = 'loading' | 'allowed' | 'locked';

type CreateResumeProfileGuardProps = {
   children: ReactNode;
};

type CreateResumeProfileGuardLocationState = {
   createResumeProfileAccess?: GuardState;
};

export const CreateResumeProfileGuard = ({ children }: CreateResumeProfileGuardProps) => {
   const rootStore = useRootStore();
   const location = useLocation();
   const navigationState = location.state as CreateResumeProfileGuardLocationState | null;
   const initialState = navigationState?.createResumeProfileAccess;
   const [state, setState] = useState<GuardState>(
      initialState === 'allowed' || initialState === 'locked' ? initialState : 'loading'
   );

   useEffect(() => {
      if (initialState === 'allowed' || initialState === 'locked') {
         return;
      }

      let isActive = true;

      const checkProfile = async () => {
         try {
            const access = await loadCreateResumeProfileAccess();

            if (!isActive) {
               return;
            }

            if (access.user) {
               rootStore.userStore.setUser(access.user);
            }

            setState(access.allowed ? 'allowed' : 'locked');
         } catch {
            if (isActive) {
               setState('locked');
            }
         }
      };

      checkProfile();

      return () => {
         isActive = false;
      };
   }, [initialState, rootStore]);

   if (state === 'loading') {
      return (
         <Box width="100%" minHeight="240px">
            <Flex align="center" justify="center" minHeight="240px">
               <Spinner size="3" />
            </Flex>
         </Box>
      );
   }

   if (state === 'locked') {
      return <ResumeLockScreen />;
   }

   return <>{children}</>;
};
